import { ODSColumn } from "@/atoms";
import { ODSAspectRatioKeeper } from "@/atoms/ODSAspectRatioKeeper";
import classNames from "classnames";
import React, { KeyboardEvent, useEffect, useState } from "react";

import { ODSCardImageProps } from "./ODSCardImage.types";
import {
  generateCardImageTabIndex,
  getHeadingAriaLabel,
  normalizeSizeAndPosition,
  onCardImageActionSlotClick,
  onCardImageActionSlotKeyDown,
} from "./ODSCardImage.utils";
import ODSCardImageHeading from "./ODSCardImageHeading";

import "./css/index.css";

const ODSCardImage: React.FC<ODSCardImageProps> = ({
  className,
  size: sizeProp = "large",
  imagePosition: imagePositionProp = "right",
  customHeight,
  label,
  title,
  titleProps,
  backgroundImage,
  contentSlot,
  actionSlot,
  tabIndex,
  aspectRatioKeeperProps = "43",
  //TODO: set a default value of "43" when aspectRatioKeeperProps is deleted
  backgroundImageAspectRatio,
  showLogo = false,
  logoProps,
  showAction = true,
  showContent = true,
  showLabel = true,
  action,
  backgroundImageProps,
  ...rest
}) => {
  const _backgroundImage = backgroundImageProps?.src ?? backgroundImage;

  const ariaLabel = getHeadingAriaLabel({ label, title });
  const generatedTabIndex = generateCardImageTabIndex({ tabIndex, action });

  const [size, setSize] = useState(sizeProp);
  const [imagePosition, setImagePosition] = useState(imagePositionProp);
  const aspectRatio = backgroundImageAspectRatio ?? aspectRatioKeeperProps;
  useEffect(() => {
    const { size: normalizedSize, imagePosition: normalizedImagePosition } =
      normalizeSizeAndPosition(sizeProp, imagePositionProp);
    setSize(normalizedSize);
    setImagePosition(normalizedImagePosition);
  }, [sizeProp, imagePositionProp]);

  const onClick = () => action?.();
  const onKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    if (key !== "Enter" && key !== " ") return;
    action?.();
  };

  return (
    <div
      className={classNames(
        "ODSCardImage",
        `ODSCardImage--size-${size}`,
        `ODSCardImage--imagePosition-${imagePosition}`,
        customHeight ? `ODSCardImage--customHeight` : "",
        actionSlot ? `ODSCardImage--withAction` : "",
        className
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...rest}
    >
      <div className="ODSCardImage-Background"></div>
      <ODSColumn className={classNames(`ODSCardImage-Content`)}>
        <ODSColumn className={classNames(`ODSCardImage-ContentWrapper`)}>
          {(label || title) && (
            <div className="ODSCardImage-Copy">
              {/* TODO: remove showLabel on next major release */}
              {label && showLabel ? (
                <div className="ODSCardImage-Label" aria-hidden="true">
                  {label}
                </div>
              ) : null}
              {title && (
                <ODSCardImageHeading
                  className="ODSCardImage-Heading"
                  {...titleProps}
                  tabIndex={generatedTabIndex}
                  role="button"
                  aria-label={ariaLabel}
                >
                  {title}
                </ODSCardImageHeading>
              )}
            </div>
          )}
          {/* TODO: remove showContent on next major release */}
          {contentSlot && showContent && (
            <ODSColumn className={classNames(`ODSCardImage-SlotContainer`)}>
              {contentSlot}
            </ODSColumn>
          )}
        </ODSColumn>
        {/* TODO: remove showAction on next major release */}
        {actionSlot && showAction && (
          <div
            className="ODSCardImage-Action-Slot"
            onClick={onCardImageActionSlotClick}
            onKeyDown={onCardImageActionSlotKeyDown}
          >
            {actionSlot}
          </div>
        )}
      </ODSColumn>
      {_backgroundImage && (
        <div className="ODSCardImage-ImageSection">
          {showLogo && logoProps && (
            <img
              {...{
                ...logoProps,
                className: classNames(
                  "ODSCardImage-Logo",
                  logoProps?.className
                ),
              }}
            />
          )}
          <div className="ODSCardImage-ImageContainer">
            <img
              className="ODSCardImage-Image"
              src={_backgroundImage}
              {...backgroundImageProps}
            />
          </div>

          <ODSAspectRatioKeeper aspectRatio={aspectRatio} />
        </div>
      )}
    </div>
  );
};

export default ODSCardImage;
